/**
 * Scoring Guide — static lesson at /my/learn/scoring.
 *
 * Explains how the project / portfolio / org health scores are
 * computed, why they aggregate the way they do, and gives examples.
 * Also covers how to organise projects + portfolios for a real
 * product.
 *
 * Linked from:
 *   - /my/learn (index card)
 *   - org dashboard "Learn more" link
 *   - portfolio dashboard "Learn more" link
 */
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, AlertTriangle, FolderTree } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const SEVERITY_PENALTY: Array<{ severity: string; penalty: number; tone: string; example: string }> = [
  { severity: 'Critical', penalty: 25, tone: 'bg-red-500/15 text-red-600 dark:text-red-400',     example: 'RCE, auth bypass, exposed secrets in production' },
  { severity: 'High',     penalty: 15, tone: 'bg-orange-500/15 text-orange-600 dark:text-orange-400', example: 'SQLi, XSS in authenticated paths, broken access control' },
  { severity: 'Medium',   penalty: 5,  tone: 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-500', example: 'Missing security headers, weak TLS cipher suite' },
  { severity: 'Low',      penalty: 2,  tone: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',   example: 'Outdated dependency without known exploit, verbose error pages' },
  { severity: 'Info',     penalty: 0,  tone: 'bg-muted text-muted-foreground',                    example: 'Tooling notes, recommendations without security impact' },
];

export function ScoringGuidePage() {
  return (
    <div className="space-y-8 max-w-4xl">
      {/* Page header */}
      <div className="space-y-3">
        <Link to="/my/learn" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to Learn
        </Link>
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
            <ShieldCheck className="h-5 w-5 text-primary-strong" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">How security scores work</h1>
            <p className="text-sm text-muted-foreground">
              Trinetra scores every project, portfolio, and org from 0–100. The math is simple and intentional —
              this guide shows you how it's calculated, why it aggregates the way it does, and how to organise your
              projects so the numbers stay meaningful.
            </p>
          </div>
        </div>
      </div>

      {/* Section 1 — The penalty model */}
      <section className="space-y-3">
        <h2 className="text-lg font-bold text-foreground">1. The penalty model</h2>
        <p className="text-sm text-foreground/90">
          Every <em>open</em> finding costs the score a fixed number of points based on its severity. The score is
          whatever's left after subtracting all the penalties from a starting balance of 100 — and it's clamped
          between 0 and 100.
        </p>

        <Card className="p-4 bg-muted/30 font-mono text-sm">
          <span className="text-muted-foreground">penalty</span> = critical × 25 + high × 15 + medium × 5 + low × 2<br/>
          <span className="text-muted-foreground">score</span>   = max(0, min(100, 100 − penalty))
        </Card>

        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Severity</th>
                <th className="text-left py-2 px-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Penalty</th>
                <th className="text-left py-2 px-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Typical findings</th>
              </tr>
            </thead>
            <tbody>
              {SEVERITY_PENALTY.map((row) => (
                <tr key={row.severity} className="border-b border-border/60">
                  <td className="py-2 px-3">
                    <span className={cn('inline-block px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider', row.tone)}>{row.severity}</span>
                  </td>
                  <td className="py-2 px-3 font-mono">−{row.penalty}</td>
                  <td className="py-2 px-3 text-muted-foreground">{row.example}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground italic">
          Findings marked <strong>dismissed</strong>, <strong>accepted-risk</strong>, or <strong>fixed</strong> drop out of the math instantly. Triage carries weight.
        </p>
      </section>

      {/* Section 2 — Project-level worked examples */}
      <section className="space-y-3">
        <h2 className="text-lg font-bold text-foreground">2. Project score — worked examples</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ExampleCard
            title="Clean project"
            score={100}
            findings="0 open"
            calc="100 − 0 = 100"
            note="No open findings means no penalty. New projects start here, and stay here as long as nothing trips a scanner or agent."
          />
          <ExampleCard
            title="One sub-critical regression"
            score={85}
            findings="1 high"
            calc="100 − 15 = 85"
            note="A single missing-auth finding takes you out of the green band. High-severity issues are the most common reason a score drops out of the 90s."
          />
          <ExampleCard
            title="Stack of mid-severity"
            score={75}
            findings="3 medium + 5 low + 2 info"
            calc="100 − (3×5 + 5×2 + 2×0) = 75"
            note="Several lower-severity findings can add up to a meaningful penalty. Prioritise by severity, but don't ignore mediums in clusters."
          />
          <ExampleCard
            title="Severe + neglected"
            score={0}
            findings="2 critical + 3 high + 5 medium"
            calc="100 − (50 + 45 + 25) = 0 (clamped)"
            note="Beyond about 100 points of penalty the score floors at 0. This isn't a tiebreaker — it's a clear 'stop shipping, fix this now' signal."
          />
        </div>
      </section>

      {/* Section 3 — Aggregation */}
      <section className="space-y-3">
        <h2 className="text-lg font-bold text-foreground">3. Portfolio &amp; org scores aren't averages</h2>
        <p className="text-sm text-foreground/90">
          When Trinetra rolls up to a portfolio or org, it does <strong>not</strong> average the project scores.
          Instead, it sums every open finding across every project in scope and runs the same penalty formula on the
          combined total. This is deliberate — security is about <em>risk concentration</em>, not average comfort. A
          single exploitable finding doesn't get diluted by perfect sibling projects.
        </p>

        <Card className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-saffron" />
            <p className="text-sm font-semibold text-foreground">Worked example</p>
          </div>
          <div className="text-sm space-y-2 text-foreground/90">
            <p>You have a portfolio with two projects:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Project A</strong> — 0 findings → score <strong>100</strong></li>
              <li><strong>Project B</strong> — 1 high + 4 low (penalty 15 + 8 = 23) → score <strong>77</strong></li>
            </ul>
            <p>The portfolio score is <strong>not</strong> (100 + 77) / 2 = 88.5. It's:</p>
            <Card className="p-3 bg-muted/30 font-mono text-sm">
              total findings: 1 high + 4 low (across both projects)<br/>
              penalty       : 15 + 8 = 23<br/>
              portfolio     : 100 − 23 = <strong>77</strong>
            </Card>
            <p className="text-xs text-muted-foreground">
              The clean project doesn't lift the score — it just contributes nothing. Project B's findings carry their full weight at every level above the project.
            </p>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="h-4 w-4 text-india-green" />
            <p className="text-sm font-semibold text-foreground">Why this design</p>
          </div>
          <ul className="text-sm text-foreground/90 space-y-2 list-disc pl-6">
            <li><strong>Honest about unfixed risks.</strong> "What's my worst exposure right now?" is the question security people actually ask. Averaging masks it.</li>
            <li><strong>Resistant to greenwashing.</strong> A team can't lift their org score by spinning up empty projects.</li>
            <li><strong>Industry-aligned.</strong> Snyk, Wiz, and most AppSec dashboards score risk-concentration this way for the same reason.</li>
          </ul>
          <p className="text-xs text-muted-foreground italic mt-3">
            The trade-off: a portfolio with 100 perfect projects and 1 bad one will read about the same as one with just the bad project. That's intentional — broad cleanliness doesn't fix a concentrated problem.
          </p>
        </Card>
      </section>

      {/* Section 4 — Organisation guidance */}
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <FolderTree className="h-5 w-5 text-india-green" />
          <h2 className="text-lg font-bold text-foreground">4. How to organise projects + portfolios</h2>
        </div>
        <p className="text-sm text-foreground/90">
          Because scores aggregate strictly upward, your portfolio shape directly affects what dashboards say. The
          rule of thumb: <strong>group things that share an owner and a release cadence</strong>; separate things
          that don't.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Card className="p-4 space-y-2">
            <p className="text-sm font-bold text-foreground">One product → one portfolio</p>
            <p className="text-xs text-muted-foreground">
              A SaaS product with multiple repos: web frontend, backend API, mobile app, infrastructure-as-code,
              shared component library — all of them are "shipping the same product" and should sit in one portfolio.
              Owner: the product team. Score answers: <em>"is this product shippable?"</em>
            </p>
            <p className="text-[11px] text-muted-foreground font-mono mt-2">
              Acme Checkout/<br/>
              ├─ acme-web<br/>
              ├─ acme-api<br/>
              ├─ acme-mobile<br/>
              ├─ acme-infra<br/>
              └─ acme-ui-kit
            </p>
          </Card>

          <Card className="p-4 space-y-2">
            <p className="text-sm font-bold text-foreground">Customer-facing vs internal — separate portfolios</p>
            <p className="text-xs text-muted-foreground">
              An exposed marketing site has a different threat model than the internal admin tool. Different blast
              radius, different release cadence, different owners. Don't merge them — the worse one will dominate
              the better one's score, and you lose the signal you need to triage.
            </p>
            <p className="text-[11px] text-muted-foreground font-mono mt-2">
              Customer-Facing/<br/>
              ├─ marketing-site<br/>
              └─ checkout-app<br/>
              Internal Tools/<br/>
              ├─ admin-console<br/>
              └─ ops-dashboard
            </p>
          </Card>

          <Card className="p-4 space-y-2">
            <p className="text-sm font-bold text-foreground">Compliance scope — separate portfolios</p>
            <p className="text-xs text-muted-foreground">
              If a subset of your code touches PCI cardholder data, GDPR personal data, or HIPAA PHI, isolate it.
              Auditors look at scope; mixing in non-regulated repos drags the regulated portfolio's posture down with
              irrelevant findings, and lifts the non-regulated one with irrelevant attestations.
            </p>
            <p className="text-[11px] text-muted-foreground font-mono mt-2">
              PCI-Scope/<br/>
              ├─ payment-service<br/>
              └─ vault-proxy<br/>
              Non-PCI/<br/>
              └─ everything-else
            </p>
          </Card>

          <Card className="p-4 space-y-2">
            <p className="text-sm font-bold text-foreground">Shared library — its own project, lift to a portfolio when consumers diverge</p>
            <p className="text-xs text-muted-foreground">
              A shared component library used by 3+ products belongs as its own project. Once consumer teams have
              independent release cadences, lift it into its own portfolio with its own ownership — its security
              posture becomes a supply-chain concern, not a feature concern.
            </p>
          </Card>

          <Card className="p-4 space-y-2">
            <p className="text-sm font-bold text-foreground">Fork / sandbox / experimental — separate portfolio</p>
            <p className="text-xs text-muted-foreground">
              Hackathon repos, archived spikes, and ML experiments shouldn't share a portfolio with production code.
              Their findings are noisy (mostly low/info) and aren't going to get fixed. Park them somewhere their
              score reflects "this is exploratory" rather than dragging the prod portfolio down.
            </p>
          </Card>

          <Card className="p-4 space-y-2">
            <p className="text-sm font-bold text-foreground">Org-level boundary — by business unit</p>
            <p className="text-xs text-muted-foreground">
              Multiple business units inside one company? Use separate <em>orgs</em>, not portfolios. Orgs are the
              tenancy boundary — billing, BYOK, member roles, audit logs all live there. Two BUs sharing an org will
              fight over those things.
            </p>
          </Card>
        </div>
      </section>

      {/* Section 5 — Action steps */}
      <section className="space-y-3">
        <h2 className="text-lg font-bold text-foreground">5. What to do with the score</h2>
        <Card className="p-5">
          <ul className="text-sm space-y-3 text-foreground/90">
            <li><strong>90 +</strong> — Healthy. Keep scanners and agents on a schedule. Re-run after every release.</li>
            <li><strong>70 – 89</strong> — Backlog forming. Triage at least every sprint; resolve highs in days, mediums in weeks.</li>
            <li><strong>50 – 69</strong> — Active risk. Stop adding features in sensitive paths until at least the criticals are fixed or accepted with a documented compensating control.</li>
            <li><strong>0 – 49</strong> — Compliance risk. Mostly a sign of long-neglected scanning, not necessarily an emergency — but treat it as one until you've classified what's actually dangerous.</li>
          </ul>
        </Card>
      </section>

      <p className="text-xs text-muted-foreground italic">
        Penalties haven't changed since launch and are intentionally simple — the score is a signal to start a conversation, not the conversation itself.
      </p>
    </div>
  );
}

function ExampleCard({ title, score, findings, calc, note }: { title: string; score: number; findings: string; calc: string; note: string }) {
  const tone =
    score >= 90 ? 'text-india-green'
    : score >= 70 ? 'text-saffron'
    : score >= 50 ? 'text-orange-600 dark:text-orange-400'
    : 'text-red-600 dark:text-red-400';
  return (
    <Card className="p-4 space-y-2">
      <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{title}</p>
      <div className="flex items-baseline gap-3">
        <span className={cn('text-4xl font-bold tabular-nums', tone)}>{score}</span>
        <span className="text-xs text-muted-foreground">/ 100</span>
      </div>
      <p className="text-xs text-muted-foreground">Findings: <span className="text-foreground">{findings}</span></p>
      <p className="text-xs text-muted-foreground font-mono">{calc}</p>
      <p className="text-xs text-foreground/80 mt-1">{note}</p>
    </Card>
  );
}
